import { LastThingEvent } from '@eagle/core-data-types';
import { Card, CardContent, CardHeader, IconButton, styled, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography } from '@ethings/ui-toolkit';
import { get, isNull, isString } from 'lodash';
import { DateTime, DurationLike } from 'luxon';
import { Children, FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { LastThingEvents } from '../../hooks';
import { formatValue, getEventTranslation } from '../../util';
import { FormatTimestamp } from '../format';
import { Icon } from '../icons';
import { MicroPortalConfig, MicroPortalLauncher } from '../micro-portal';
import { DateTimeTooltip } from '../tooltip';

interface EventSettings {
  feature: string;
  label?: ReactNode | string;
  eventTypeId: string;
  path: string;
  formatter?: string;
  isEditable?: boolean;
  microPortalConfig?: MicroPortalConfig;
}

interface RecentEventCardProps {
  'data-testid'?: string;
  headers?: ReactNode;
  customRows?: ReactNode[];
  eventRows: EventSettings[];
  lastEvents?: LastThingEvents;
  title?: string;
  range?: DurationLike;
  thingId?: string;
}

interface EventRowProps {
  feature: string;
  lastEventFeature: LastThingEvent;
  path: string;
  label?: ReactNode | string;
  formatter?: string;
  range?: DurationLike;
  microPortalConfig?: MicroPortalConfig;
  isEditable?: boolean;
  thingId?: string;
}

const DEFAULT_RANGE = { days: 30 };

const StyledCardContent = styled(CardContent)({
  padding: 0,
  paddingBottom: 0,
  '&&:last-child': {
    paddingBottom: 0,
  },
});

const StyledTableRow = styled(TableRow)({
  '&:last-child td, &:last-child th': {
    borderBottom: 0,
  },
});

const NoEventsCard: FC<{ title?: string; 'data-testid'?: string }> = ({ title, ...props }) => {
  const { t } = useTranslation(['common']);
  return <Card>
    <CardHeader title={title || t('common:component.recent_metrics.label')} />
    <StyledCardContent>
      <Table style={{ width: '100%' }} data-testid={props['data-testid']}>
        <StyledTableRow><TableCell><Typography fontStyle="italic">{t('common:common.hint.list.no-results')}</Typography></TableCell></StyledTableRow>
      </Table>
    </StyledCardContent>
  </Card>;
};

const NoRecentRow: FC<{ label: ReactNode | string; duration: DurationLike }> = ({ label, duration }) => {
  const { t } = useTranslation(['common']);
  const durationKey = Object.keys(duration)[0] as keyof DurationLike;
  const durationValue = duration[durationKey] as string;
  return <StyledTableRow>
    <TableCell>{label}</TableCell>
    <TableCell><Typography variant='body2' fontStyle="italic" color="text.secondary">{t('common:component.recent-events-card.hint.no-data', { value: durationValue, measurement: durationKey })}</Typography></TableCell>
    <TableCell align='right'>&nbsp;</TableCell>
  </StyledTableRow>;
};

const EventRow: FC<EventRowProps> = ({ feature, lastEventFeature, path, label, formatter, range = DEFAULT_RANGE, microPortalConfig, isEditable, thingId }) => {
  const { t } = useTranslation(['common']);
  const { featureLabel } = getEventTranslation(feature, lastEventFeature.eventTypeId);
  const reading = get(lastEventFeature, path) as string | number;
  const rowLabel = label || featureLabel || '';
  const rangeLimit = DateTime.now().minus(range).toJSDate();

  if (isNull(reading) || (lastEventFeature.occurred.getTime() <= rangeLimit.getTime())) {
    return <NoRecentRow label={rowLabel} duration={range} />;
  }

  const readingNumber: number = isString(reading) ? parseFloat(reading) : reading;
  const value = formatter ? formatValue(readingNumber, formatter) : readingNumber;
  const canLaunchMicroPortal = microPortalConfig && isEditable && thingId;

  return (
    <StyledTableRow>
      <TableCell>{rowLabel}</TableCell>
      <TableCell>
        {value}
        {canLaunchMicroPortal && (
          <MicroPortalLauncher
            app={microPortalConfig.app}
            type={microPortalConfig.type}
            data={{
              ...microPortalConfig.data,
              thingId,
            }}
            element={
              <Tooltip title={microPortalConfig.action.tooltip || t('common:terms.modify')}>
                <IconButton
                  size='small'
                  sx={{ color: 'text.disabled', '&:hover': { color: 'text.secondary' }, ml: 1 }}
                  aria-label={microPortalConfig.action.tooltip || t('common:terms.modify')}
                >
                  <Icon icon={microPortalConfig.action.icon || 'Cog'} style={{ fontSize: '1rem' }} />
                </IconButton>
              </Tooltip>
            }
            isDismissible={true}
          />
        )}
      </TableCell>
      <TableCell align='right'>
        <DateTimeTooltip variant='body2' sx={{ display: 'inline' }} display={<FormatTimestamp format='relative' value={lastEventFeature.occurred} />} title={<FormatTimestamp format='long' value={lastEventFeature.occurred} />} />
      </TableCell>
    </StyledTableRow>
  );
};

export const RecentEventCard: FC<RecentEventCardProps> = ({ customRows = [], eventRows, lastEvents, title, headers, range = DEFAULT_RANGE, thingId, ...props }) => {
  const { t } = useTranslation(['common']);
  const rows: ReactNode[] = Children.toArray(customRows);

  if (lastEvents) {
    eventRows.forEach((row) => {
      const eventTypeId = row.eventTypeId;
      const features = Object.keys(lastEvents.byFeature);

      const matchingFeatures = features.filter((feature) =>
        feature === row.feature &&
        lastEvents.byFeature[feature][eventTypeId]?.eventTypeId === eventTypeId,
      );

      matchingFeatures.forEach((feature) => {
        const lastEventFeature = lastEvents.byFeature[feature][eventTypeId];
        rows.push(<EventRow key={`${feature}-${eventTypeId}`} feature={feature} lastEventFeature={lastEventFeature} path={row.path} label={row.label} formatter={row.formatter} range={range} microPortalConfig={row.microPortalConfig} isEditable={row.isEditable} thingId={thingId} />);
      });
    });
  }

  if (rows.length === 0) {
    return <NoEventsCard />;
  }

  return <Card>
    <CardHeader title={title || t('common:component.recent_metrics.label')} />
    <StyledCardContent>
      <Table style={{ width: '100%' }} data-testid={props['data-testid']}>
        <TableHead>{headers}</TableHead>
        <TableBody>{rows}</TableBody>
      </Table>
    </StyledCardContent>
  </Card>;
};
